/**
 * Utilitários de pendências operacionais (somente apresentação).
 */
export interface PendencyItem {
  id: string;
  label: string;
  kind: 'checklist' | 'field' | 'justification' | 'attachment';
}

export function isFieldValueEmpty(value: unknown): boolean {
  return value === '' || value === undefined || value === null;
}

export function getPendingRequiredFieldIds(
  fields: Array<{ id: string; required?: boolean }>,
  formState: Record<string, unknown>,
): string[] {
  return fields
    .filter((field) => field.required && isFieldValueEmpty(formState[field.id]))
    .map((field) => field.id);
}

export function buildPendencyItems(args: {
  checklist: Array<{ id: string; label: string; required?: boolean }>;
  checklistState: Record<string, boolean>;
  validationErrors: Array<{ fieldId?: string; message: string }>;
  visibleFields: Array<{ id: string; label: string; required?: boolean }>;
  formState: Record<string, unknown>;
  requireJustification: boolean;
  hasJustification: boolean;
  requireAttachment: boolean;
  attachmentCount: number;
}): PendencyItem[] {
  const items: PendencyItem[] = [];

  for (const item of args.checklist) {
    if (item.required && !args.checklistState[item.id]) {
      items.push({ id: `chk-${item.id}`, label: item.label, kind: 'checklist' });
    }
  }

  const pendingFieldIds = new Set(getPendingRequiredFieldIds(args.visibleFields, args.formState));
  for (const fieldId of pendingFieldIds) {
    const field = args.visibleFields.find((f) => f.id === fieldId);
    items.push({
      id: `fld-${fieldId}`,
      label: field?.label ?? 'Campo obrigatório',
      kind: 'field',
    });
  }

  for (const error of args.validationErrors) {
    if (error.fieldId && pendingFieldIds.has(error.fieldId)) continue;
    const exists = items.some((i) => i.id === `err-${error.fieldId ?? error.message}`);
    if (!exists) {
      items.push({
        id: `err-${error.fieldId ?? error.message}`,
        label: error.message,
        kind: 'field',
      });
    }
  }

  if (args.requireJustification && !args.hasJustification) {
    items.push({ id: 'justification', label: 'Justificativa obrigatória', kind: 'justification' });
  }

  if (args.requireAttachment && args.attachmentCount <= 0) {
    items.push({ id: 'attachment', label: 'Anexo obrigatório', kind: 'attachment' });
  }

  return items;
}

export function pendenciesSemaphoreTone(count: number): 'ok' | 'warning' | 'danger' {
  if (count <= 0) return 'ok';
  if (count <= 3) return 'warning';
  return 'danger';
}

export function problemsSemaphoreTone(count: number): 'ok' | 'danger' {
  return count > 0 ? 'danger' : 'ok';
}

export function primaryActionSubtitle(args: {
  canComplete: boolean;
  pendenciesCount: number;
  pendingChecklistCount: number;
  pendingFieldCount: number;
  primaryLabel?: string;
}): string | null {
  const isFinalize = args.primaryLabel?.toLowerCase().includes('finalizar');

  if (args.pendenciesCount <= 0) {
    if (args.canComplete && isFinalize) return 'PRONTO PARA CONCLUIR';
    return null;
  }

  if (args.pendingChecklistCount > 0 && args.pendingFieldCount === 0) {
    return 'CHECKLIST INCOMPLETO';
  }

  if (isFinalize) {
    return `${args.pendenciesCount} PENDÊNCIA${args.pendenciesCount > 1 ? 'S' : ''} ABERTA${args.pendenciesCount > 1 ? 'S' : ''}`;
  }

  return `${args.pendenciesCount} PENDÊNCIA${args.pendenciesCount > 1 ? 'S' : ''} PARA CONCLUSÃO`;
}
